import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Input,
  Badge,
  Spinner,
} from "reactstrap";
import { useTranslation } from "context/TranslationProvider";
import helpers from "utils/helpers";
import Zoom from "react-medium-image-zoom";

const ProfilePictureModal = ({
  isOpen,
  toggle,
  user,
  profilePicUrl,
  onModerate,
  loading,
}) => {
  const { t } = useTranslation();

  const currentStatus = helpers.normalizeOrderStatus(
    user?.profilePictureModeration?.status
  );

  const isPending = currentStatus === helpers.LISTING_TABLE_FILTERS.PENDING;
  const isApproved = currentStatus === helpers.LISTING_TABLE_FILTERS.APPROVED;

  const [adminRemarks, setAdminRemarks] = useState("");

  useEffect(() => {
    setAdminRemarks(user?.profilePictureModeration?.adminRemarks || "");
  }, [user?.profilePictureModeration?.adminRemarks]);

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle} className="pb-2">
        {t("page.users.profilePictureReview.title", "Profile Picture Review")}
      </ModalHeader>

      <ModalBody>
        <div className="text-center mb-3">
          {profilePicUrl ? (
            <Zoom>
            <img
              src={profilePicUrl}
              alt="Profile"
              className="rounded shadow"
              style={{ maxWidth: "100%", maxHeight: 320 }}
            />
            </Zoom>
          ) : (
            <p className="text-muted mb-0">
              {t("page.users.profilePictureReview.noImage", "No profile picture uploaded")}
            </p>
          )}
        </div>

        <div className="d-flex justify-content-center mb-3">
          <Badge
            color={
              isApproved
                ? "success"
                : currentStatus === helpers.LISTING_TABLE_FILTERS.REJECTED
                ? "danger"
                : "warning"
            }
            pill
          >
            {t(`page.userListings.status.${currentStatus}`)}
          </Badge>
        </div>

        <FormGroup>
          <label className="form-control-label text-sm">
            {t("page.listing.adminRemarks", "Admin Remarks")}
          </label>
          <Input
            type="textarea"
            rows="4"
            className="form-control-alternative"
            placeholder={t("page.listing.remarksPlaceholder", "Add remarks here...")}
            value={adminRemarks}
            disabled={!isPending || loading}
            onChange={(e) => setAdminRemarks(e.target.value)}
          />
        </FormGroup>

        {!isPending && (
          <p className="text-sm text-muted mb-0">
            {isApproved
              ? t("page.users.profilePictureReview.alreadyApproved", "This profile picture has already been approved.")
              : t("page.listing.review.locked", "Remarks are locked once the listing is reviewed or expired.")}
          </p>
        )}
      </ModalBody>

      {isPending && (
        <ModalFooter className="pt-2">
          <Button
            color="danger"
            outline
            disabled={loading}
            onClick={() =>
              onModerate(helpers.LISTING_TABLE_FILTERS.REJECTED, adminRemarks)
            }
          >
            {loading === helpers.LISTING_TABLE_FILTERS.REJECTED ? (
              <>
                <Spinner size="sm" className="mr-2" />
                {t("page.offerListings.status.reject", "Reject")}
              </>
            ) : (
              t("page.offerListings.status.reject", "Reject")
            )}
          </Button>

          <Button
            color="success"
            disabled={loading}
            onClick={() =>
              onModerate(helpers.LISTING_TABLE_FILTERS.APPROVED, adminRemarks)
            }
          >
            {loading === helpers.LISTING_TABLE_FILTERS.APPROVED ? (
              <>
                <Spinner size="sm" className="mr-2" />
                {t("page.userListings.status.approved", "Approved")}
              </>
            ) : (
              t("page.userListings.status.approved", "Approved")
            )}
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};

export default ProfilePictureModal;
